import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api/api";

export const CancelOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusOrderId = searchParams.get("orderId") || null;

  // who am I?
  const me = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("sp_user") || "{}"); } catch { return {}; }
  }, []);
  const role = me?.role;
  const isBuyer  = role === "user" || role === "buyer";
  const isVendor = role === "vendor" || me?.isVendor === true;

  // state
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setErr("");

      // fetch the relevant orders for this role
      const res  = isVendor ? await api.orders.vendorMine() : await api.orders.mine();
      const list = Array.isArray(res?.orders) ? res.orders : Array.isArray(res) ? res : [];

      // cancelled only
      const cancelled = list.filter(o => o.status === "cancelled");

      // pick focused id if provided, else first cancelled
      const found = focusOrderId
        ? cancelled.find(o => (o._id || o.orderId) === focusOrderId)
        : cancelled[0];

      setOrder(found || null);
    } catch (e) {
      setErr(e?.data?.message || e?.data?.error || e?.message || "Failed to load cancelled order");
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBuyer, isVendor, focusOrderId]);

  // helpers
  const fmtNaira = (n) => `₦${Number(n || 0).toLocaleString()}`;

  const orderNumber = order?.orderId || order?._id || "";
  const createdAt   = order ? new Date(order.createdAt || order.timestamp || Date.now()).toLocaleString() : "";
  const total       = order ? fmtNaira(order.totalAmount) : "₦0";

  // phones (per role)
  const buyerPhone = order?.buyerPhone || order?.customerPhone || "-";
  const vendorPhone = (() => {
    const items = Array.isArray(order?.items) ? order.items : [];
    const prod = items[0]?.productId && typeof items[0].productId === "object" ? items[0].productId : null;
    // be tolerant to different field names from product creation
    return prod?.sellerPhone || prod?.vendorPhone || prod?.phone || "-";
  })();


  // names (best-effort; orders usually don’t store buyer name/email unless you added it)
  const buyerName  = order?.buyerName || order?.customerName || me?.userName || "Buyer";
  const buyerEmail = order?.buyerEmail || order?.customerEmail || "-";

  return (
    <div className="min-h-screen grid place-items-center bg-[#fff8e1]">
      <div className="app-container [font-family:'Nunito',Helvetica] text-[#5d4037] pb-40">
        {/* Topbar */}
        <div className="w-full flex items-center mt-4">
          <button onClick={() => navigate(-1)} className="ml-4 w-9 h-9 rounded-full bg-[#ccb357] grid place-items-center" aria-label="Back">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <h1 className="flex-1 text-center font-bold text-[18px]">Canceled Order</h1>
          <div className="w-9" />
        </div>

        {/* Order number */}
        <div className="px-4 mt-3 text-[14px]">
          {orderNumber ? `Order #${orderNumber}` : (loading ? "" : "No cancelled order found")}
        </div>

        {/* Customer summary (dynamic) */}
        <div className="px-4 mt-4">
          <div className="relative rounded-[12px] bg-[#eadfb7] p-3 overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#c2452d]" />
            <div className="flex items-start justify-between pl-3">
              <div>
                {/* Show name/email/phone; prefer buyer info on vendor view, and vendor phone for buyer view */}
                <p className="font-bold">
                  {isVendor ? (buyerName || "Buyer") : (order?.items?.[0]?.productId?.sellerName || "Vendor")}
                </p>
                <p className="text-[13px] mt-1">
                  {isVendor ? buyerEmail : (order?.items?.[0]?.productId?.sellerEmail || "-")}
                </p>
                <p className="text-[13px]">{createdAt || "-"}</p>
                <p className="text-[13px]">
                  {isVendor ? buyerPhone : vendorPhone}
                </p>

                <span className="inline-block mt-2 text-[12px] px-2 py-1 rounded-full bg-[#f5c5c1] text-[#a23526]">
                  Cancelled
                </span>
              </div>
              <p className="font-bold">{total}</p>
            </div>
          </div>
        </div>

        {/* Address (dynamic) */}
        <div className="px-4 mt-5">
          <p className="font-extrabold">Delivery Address</p>
          <div className="mt-2 rounded-[12px] bg-[#eadfb7] p-3">
            <p>{order?.shippingAddress?.street || "-"}</p>
            <p>
              {[
                order?.shippingAddress?.city || "",
                order?.shippingAddress?.state || "",
              ].filter(Boolean).join(", ") || "-"}
            </p>
            <p>{order?.shippingAddress?.country || "-"}</p>
          </div>
        </div>

        {/* Items (dynamic) */}
        <div className="px-4 mt-5">
          <p className="font-extrabold">Items</p>
          <div className="mt-3 space-y-3">
            {(order?.items || []).map((it, i) => {
              const prod  = it.productId && typeof it.productId === "object" ? it.productId : null;
              const img   = prod?.images?.[0] || "https://via.placeholder.com/48x48?text=IMG";
              const name  = it?.name || prod?.name || "Item";
              const price = fmtNaira(it?.pricePerUnit);
              return (
                <div key={i} className="rounded-[10px] bg-[#eadfb7] px-2 py-2 flex items-center">
                  <img alt="" src={img} className="w-12 h-12 rounded-[8px] object-cover" />
                  <div className="ml-3">
                    <p className="font-bold text-[14px]">{name}</p>
                    <p className="text-[12px]">{price}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions (keep layout; no status change available on cancelled) */}
        <div className="px-4 mt-8 flex flex-col items-center">
          <button
            onClick={() => navigate("/orders")}
            className="w-[290px] h-[56px] rounded-[28px] bg-[#f7c945] text-[#5d4037] font-bold hover:opacity-90"
          >
            Back to Orders
          </button>
          <button onClick={() => navigate(-1)} className="mt-4 text-[#c34040] underline">
            Cancel
          </button>
        </div>

        {/* Bottom bar */}
        <div className="fixed bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 w-full max-w-[1440px] xl:max-w-[1600px] px-6 md:px-10 xl:px-16">
          <div className="h-[22px] bg-[#5d4037] flex items-center justify-center">
            <div className="w-[120px] h-[6px] rounded-full bg-white" />
          </div>
        </div>
      </div>
    </div>
  );
};
